import { Menu, MenuTrigger, Button, MenuPopover, MenuList, MenuItem, MenuDivider } from '@fluentui/react-components';
import React from 'react';
import { useAppActions, useAppState } from '../../context';
import { DialogContextState, useDialogActions } from '../../context/dialogContext';
import { getPageType, pageTypeNationales } from '../../utils/orderDocumentationHelpers';
import { isPageReferenceActivityGroup, isPageReferenceStep } from '@gkn-ili/views/dist/OrderDocumentation';
import { exportTimeSheetWorkbook } from '../../utils/exporters/timesheet';

export default function AppHeaderMenu(): JSX.Element {
  const appState = useAppState();
  const actions = useAppActions();
  const dialogActions = useDialogActions();

  const openFileDialogRef = React.useRef<HTMLInputElement>(null);

  // Edit and delete dialogs
  const pageRef = appState.activePage;
  const pageType = getPageType(pageRef);
  const pageTypeNationale = pageTypeNationales[pageType];
  const editDialogPayload: DialogContextState = isPageReferenceActivityGroup(pageRef)
    ? {
        type: 'editActivityGroup',
        data: {
          activityGroupReference: pageRef,
        },
      }
    : isPageReferenceStep(pageRef)
      ? { type: 'editStep', data: { stepReference: pageRef } }
      : {
          type: 'editActivity',
          data: {
            activityReference: pageRef,
          },
        };
  const deleteDialogPayload: DialogContextState = {
    type: 'delete',
    data: {
      reference: pageRef,
    },
  };

  const openFileFormats = {
    order: '.zip',
    '3dxml': '.3dxml',
  };

  const currentRoutingItemForExport = appState.orderView.routing[appState.activePage.routingItemKey];

  return (
    <>
      <input
        type="file"
        hidden
        ref={openFileDialogRef}
        onChange={async (event) => {
          const file = event.target.files![0];
          if (!file) {
            return;
          }
          if (event.target.accept === openFileFormats.order) {
            await actions.importOrder(file);
          } else if (event.target.accept === openFileFormats['3dxml']) {
            await actions.replace3DXMLModel(file);
          }
        }}
      />
      <Menu>
        <MenuTrigger>
          <Button appearance="primary">File</Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem>New</MenuItem>
            <MenuItem
              onClick={() => {
                if (openFileDialogRef.current) {
                  openFileDialogRef.current.value = '';
                  openFileDialogRef.current.accept = openFileFormats.order;
                  openFileDialogRef.current.click();
                }
              }}
            >
              Open from local storage
            </MenuItem>
            <MenuItem onClick={() => actions.exportOrder()}>Save to local storage</MenuItem>
            <MenuDivider />
            <MenuItem
              onClick={() => {
                if (openFileDialogRef.current) {
                  openFileDialogRef.current.value = '';
                  openFileDialogRef.current.accept = openFileFormats['3dxml'];
                  openFileDialogRef.current.click();
                }
              }}
            >
              Replace 3DXML model
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      <Menu>
        <MenuTrigger>
          <Button appearance="primary">Edit</Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              onClick={() => {
                dialogActions.openDialog(editDialogPayload);
              }}
            >
              Edit current {pageTypeNationale}
            </MenuItem>
            <MenuItem onClick={() => dialogActions.openDialog(deleteDialogPayload)}>
              Delete current {pageTypeNationale}
            </MenuItem>
            <MenuDivider />
            <Menu>
              <MenuTrigger>
                <MenuItem>Add</MenuItem>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    onClick={() =>
                      dialogActions.openDialog({ type: 'createStep', data: { initialPreviousReference: pageRef } })
                    }
                  >
                    Add step
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      dialogActions.openDialog({
                        type: 'createActivityGroup',
                        data: { initialPreviousReference: pageRef },
                      })
                    }
                  >
                    Add activity group
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      dialogActions.openDialog({
                        type: 'createActivity',
                        data: { initialPreviousReference: pageRef },
                      })
                    }
                  >
                    Add activity
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </MenuList>
        </MenuPopover>
      </Menu>
      <Menu>
        <MenuTrigger>
          <Button appearance="primary">Publish</Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              onClick={() => {
                throw Error('Not implemented');
              }}
            >
              Export RSR
            </MenuItem>
            <MenuItem onClick={() => exportTimeSheetWorkbook(currentRoutingItemForExport)}>Export timesheet</MenuItem>
            <MenuItem
              onClick={() => {
                throw Error('Not implemented');
              }}
            >
              Export BoM
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </>
  );
}
